import React from "react";
import {
    Droplet,
    Minus,
    Plus,
    Sun,
    Thermometer,
    ThumbsUp,
    Zap,
    AlertTriangle
} from "react-feather";

import { TbDatabaseExclamation } from "react-icons/tb";
import { MdHistoryToggleOff } from "react-icons/md";
import { AiOutlineAlert, AiOutlineSignature } from "react-icons/ai";

import { Badge } from "reactstrap";
import { Badge as AntdBadge, Tooltip } from "antd";

import * as cleanTextUtils from "clean-text-utils";
import i18next from "i18next";

import {
    getActionsColumn,
    getColumnTableProduction,
    getDeleteColumn,
    getGlobalStatusColumn,
    getLocationColumn,
    getMaxColumn,
    getMinColumn,
    getNameColumn,
    getPerformanceColumn,
    getProductionColumn
} from "components/Table/Columns";

import _ from "lodash";

const textCleaner = require("text-cleaner");


/*
 * ============================================================
 * TYPES
 * ============================================================
 */

export const _SOLAR_THERMAL = "solarThermal";
export const _SOLAR_PHOTOVOLTAIC = "photovoltaic";
export const _THERMAL = "thermal";
export const _WATER = "water";
export const _GRAY_WATER = "gray_water";

export const _GLOBAL_STATUS = "global_status";
export const _STATUS = "status";
export const _ALERTS = "alerts";

export const _DASHBOARD = "dashboard";


/*
 * ============================================================
 * COLORS
 * ============================================================
 */

export const solarThermalColor = "#fac108";
export const solarPhotovoltaicColor = "#f89f43";
export const thermalColor = "#b9de47";
export const waterColor = "#54c8f1";

export const solarThermalProgressBarClass =
    "progress-bar-solar-thermal";

export const solarPhotovoltaicProgressBarClass =
    "progress-bar-warning";

export const solarThermalBgClass =
    "bg-light-thermal";

export const solarPhotovoltaicBgClass =
    "bg-light-photovoltaic";

export const waterBgClass =
    "bg-light-water";


/*
 * ============================================================
 * HELPERS
 * ============================================================
 */

export function isObject(el) {
    return (
        typeof el === "object" &&
        !Array.isArray(el) &&
        el !== null
    );
}


export function isSolarThermalByCaller(props) {
    return props.caller === _SOLAR_THERMAL;
}


export function isSolarPhotovoltaicByCaller(props) {
    return props.caller === _SOLAR_PHOTOVOLTAIC;
}


export function isThermalByCaller(props) {
    return props.caller === _THERMAL;
}


export function isWaterByCaller(props) {
    return props.caller === _WATER;
}


export function isStatusByType(props) {
    return props.type === _STATUS;
}


export function isAlertsByType(props) {
    return props.type === _ALERTS;
}


export function isDashboardType(props) {
    return props.type === _DASHBOARD;
}


/*
 * ============================================================
 * PROGRESS BAR
 * ============================================================
 */

export function getProgressBarClassByCaller(props) {

    if (isSolarThermalByCaller(props)) {
        return solarThermalProgressBarClass;
    }

    return "progress-bar-warning";
}


/*
 * ============================================================
 * SECOND READING
 * ============================================================
 */

export function getSecondReading(props) {
    return props.caller.second_reading;
}


/*
 * ============================================================
 * COLORS BY CALLER
 * ============================================================
 */

export function getColorProductionByCaller(props) {

    if (isSolarThermalByCaller(props)) {
        return solarThermalColor;
    }

    if (isThermalByCaller(props)) {
        return thermalColor;
    }

    if (isWaterByCaller(props)) {
        return waterColor;
    }

    return solarPhotovoltaicColor;
}


export function getBgClassByCaller(props) {

    if (isSolarThermalByCaller(props)) {
        return solarThermalBgClass;
    }

    if (isWaterByCaller(props)) {
        return waterBgClass;
    }

    return solarPhotovoltaicBgClass;
}


/*
 * ============================================================
 * ICON BY CALLER
 * ============================================================
 */

export function getIconComponentByCaller(config, props) {

    if (isSolarThermalByCaller(config)) {
        return <Sun {...props} />;
    }

    if (isWaterByCaller(config)) {
        return <Droplet {...props} />;
    }

    if (isThermalByCaller(config)) {
        return <Thermometer {...props} />;
    }

    return <Zap {...props} />;
}


/*
 * ============================================================
 * PRODUCTION TEXT
 * ============================================================
 */

export function getUnderproductionTextByCaller(props) {

    if (isThermalByCaller(props)) {
        return "events.low_temp";
    }

    if (isWaterByCaller(props)) {
        return "events.underconsumption_abbr";
    }

    return "events.underproduction";
}


export function getOverproductionTextByCaller(props) {

    if (isThermalByCaller(props)) {
        return "events.high_temp";
    }

    if (isWaterByCaller(props)) {
        return "events.overconsumption_abbr";
    }

    return "events.overproduction";
}


/*
 * ============================================================
 * PLANT STATUS
 * ============================================================
 *
 * plant_incident_id
 *
 * 1 = OK
 * 2 = No data
 * 3 = Wrong value
 * 4 = 404
 * 5 = Overproduction
 * 6 = Underproduction
 * 7 = Waiting
 * 8 = Off
 *
 * ============================================================
 */

export function getIconByPlantStatus(
    config,
    alert,
    text = undefined
) {

    alert = parseInt(alert);


    if (
        alert === 2 ||
        alert === 3 ||
        alert === 4 ||
        alert === 8 ||
        alert === 0 ||
        alert === 7
    ) {

        return (
            <Tooltip
                placement="topLeft"
                title={i18next.t("events.no_reading")}
                overlayStyle={{
                    fontSize: "13px"
                }}
            >
                <Badge
                    color="light-danger"
                    className="badge-icon-lg"
                >
                    {text && (
                        <span className="d-flex align-items-center">

                            <TbDatabaseExclamation />

                            <span
                                style={{
                                    marginLeft: "5px"
                                }}
                            >
                                {text}
                            </span>

                        </span>
                    )}

                    {!text && (
                        <TbDatabaseExclamation />
                    )}

                </Badge>
            </Tooltip>
        );
    }


    if (alert === 5) {

        let tooltip =
            i18next.t("events.overproduction");


        if (isWaterByCaller(config)) {

            tooltip =
                i18next.t(
                    "events.overconsumption"
                );

        } else if (
            isThermalByCaller(config)
        ) {

            tooltip =
                i18next.t(
                    "events.high_temp"
                );
        }


        return (
            <Tooltip
                placement="topLeft"
                title={tooltip}
                overlayStyle={{
                    fontSize: "13px"
                }}
            >

                <Badge
                    color="light-info"
                    className="badge-icon-lg"
                >

                    {text && (
                        <span className="d-flex align-items-center">

                            <Plus />

                            <span
                                style={{
                                    marginLeft: "5px"
                                }}
                            >
                                {text}
                            </span>

                        </span>
                    )}

                    {!text && <Plus />}

                </Badge>

            </Tooltip>
        );
    }


    if (alert === 6) {

        let tooltip =
            i18next.t(
                "events.underproduction"
            );


        if (isWaterByCaller(config)) {

            tooltip =
                i18next.t(
                    "events.underconsumption"
                );

        } else if (
            isThermalByCaller(config)
        ) {

            tooltip =
                i18next.t(
                    "events.low_temp"
                );
        }


        return (
            <Tooltip
                placement="topLeft"
                title={tooltip}
                overlayStyle={{
                    fontSize: "13px"
                }}
            >

                <Badge
                    color="light-primary"
                    className="badge-icon-lg"
                >

                    {text && (
                        <span className="d-flex align-items-center">

                            <Minus />

                            <span
                                style={{
                                    marginLeft: "5px"
                                }}
                            >
                                {text}
                            </span>

                        </span>
                    )}

                    {!text && <Minus />}

                </Badge>

            </Tooltip>
        );
    }


    if (alert === 7) {

        return (
            <Tooltip
                placement="topLeft"
                title={i18next.t(
                    "events.in_process"
                )}
                overlayStyle={{
                    fontSize: "13px"
                }}
            >

                <Badge
                    color="light-warning"
                    className="badge-icon-lg badge-icon-no-stroke"
                >

                    {text && (
                        <span className="d-flex align-items-center">

                            <MdHistoryToggleOff />

                            <span
                                style={{
                                    marginLeft: "5px"
                                }}
                            >
                                {text}
                            </span>

                        </span>
                    )}

                    {!text && (
                        <MdHistoryToggleOff />
                    )}

                </Badge>

            </Tooltip>
        );
    }


    if (!config.hideEventOk) {

        return (
            <Tooltip
                placement="topLeft"
                title={i18next.t("events.ok")}
                overlayStyle={{
                    fontSize: "13px"
                }}
            >

                <Badge
                    color="light-success"
                    className="badge-icon-lg"
                >

                    {text && (
                        <span className="d-flex align-items-center">

                            <ThumbsUp />

                            <span
                                style={{
                                    marginLeft: "5px"
                                }}
                            >
                                {text}
                            </span>

                        </span>
                    )}

                    {!text && (
                        <ThumbsUp />
                    )}

                </Badge>

            </Tooltip>
        );
    }


    return "-";
}


/*
 * ============================================================
 * SECOND READING STATUS
 * ============================================================
 */

export function getIconByPlantSecond_readingStatus(
    config,
    alert,
    text = undefined
) {

    alert = parseInt(alert);


    if (
        alert === undefined ||
        alert === null ||
        alert === ""
    ) {
        return "-";
    }


    if (isNaN(alert)) {
        return "-";
    }


    /*
     * 2 = Exento
     */
    if (alert === 2) {

        return (
            <Tooltip
                placement="topLeft"
                title={i18next.t("events.ok")}
                overlayStyle={{
                    fontSize: "13px"
                }}
            >

                <Badge
                    className="badge-icon-lg"
                    style={{
                        backgroundColor: "#ffffff",
                        color: "#333333",
                        padding: "5px 10px",
                        fontWeight: "bold"
                    }}
                >

                    {text && (
                        <span className="d-flex align-items-center">

                            <span
                                style={{
                                    color: "#333333",
                                    fontWeight: "bold"
                                }}
                            >
                                U
                            </span>

                            <span
                                style={{
                                    marginLeft: "5px"
                                }}
                            >
                                {text}
                            </span>

                        </span>
                    )}

                    {!text && (
                        <span
                            style={{
                                color: "#333333",
                                fontWeight: "bold"
                            }}
                        >
                            U
                        </span>
                    )}

                </Badge>

            </Tooltip>
        );
    }


    /*
     * 1 = Alerta secundaria
     */
    if (alert === 1) {

        return (
            <Tooltip
                placement="topLeft"
                title={i18next.t("events.ok")}
                overlayStyle={{
                    fontSize: "13px"
                }}
            >

                <Badge
                    color="light-warning"
                    className="badge-icon-lg"
                >

                    {text && (
                        <span className="d-flex align-items-center">

                            <span
                                style={{
                                    color: "red",
                                    fontWeight: "bold"
                                }}
                            >
                                S
                            </span>

                            <span
                                style={{
                                    marginLeft: "5px"
                                }}
                            >
                                {text}
                            </span>

                        </span>
                    )}

                    {!text && (
                        <span
                            style={{
                                color: "red",
                                fontWeight: "bold",
                                fontSize: "18px",
                                lineHeight: "18px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            S
                        </span>
                    )}

                </Badge>

            </Tooltip>
        );
    }


    /*
     * 0 = Sin alerta secundaria
     */
    if (alert === 0) {

        return (
            <Tooltip
                placement="topLeft"
                title={i18next.t("events.ok")}
                overlayStyle={{
                    fontSize: "13px"
                }}
            >

                <Badge
                    color="light-success"
                    className="badge-icon-lg"
                >

                    {text && (
                        <span className="d-flex align-items-center">

                            <span
                                style={{
                                    color: "green",
                                    fontWeight: "bold"
                                }}
                            >
                                S
                            </span>

                            <span
                                style={{
                                    marginLeft: "5px"
                                }}
                            >
                                {text}
                            </span>

                        </span>
                    )}

                    {!text && (
                        <span
                            style={{
                                color: "red",
                                fontWeight: "bold",
                                fontSize: "18px",
                                lineHeight: "18px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            S
                        </span>
                    )}

                </Badge>

            </Tooltip>
        );
    }


    return (
        <Tooltip
            placement="topLeft"
            title={i18next.t(
                "events.no_reading"
            )}
            overlayStyle={{
                fontSize: "13px"
            }}
        >

            <Badge
                color="light-danger"
                className="badge-icon-lg"
            >

                {text && (
                    <span className="d-flex align-items-center">

                        <TbDatabaseExclamation />

                        <span
                            style={{
                                marginLeft: "5px"
                            }}
                        >
                            {text}
                        </span>

                    </span>
                )}

                {!text && (
                    <TbDatabaseExclamation />
                )}

            </Badge>

        </Tooltip>
    );
}


/*
 * ============================================================
 * SECOND READING
 * ============================================================
 */

export function getIconByPlantSecond_reading(
    config,
    alert,
    text = undefined
) {

    alert = parseInt(alert);


    if (alert === 2) {

        return (
            <Tooltip
                placement="topLeft"
                title={i18next.t("events.ok")}
                overlayStyle={{
                    fontSize: "13px"
                }}
            >

                <Badge
                    className="badge-icon-lg"
                    style={{
                        backgroundColor: "#ffffff",
                        color: "#333333",
                        padding: "5px 10px",
                        fontWeight: "bold"
                    }}
                >

                    {text && (
                        <span className="d-flex align-items-center">

                            <span
                                style={{
                                    color: "#333333",
                                    fontWeight: "bold"
                                }}
                            >
                                U
                            </span>

                            <span
                                style={{
                                    marginLeft: "5px"
                                }}
                            >
                                {text}
                            </span>

                        </span>
                    )}

                    {!text && (
                        <span
                            style={{
                                color: "#333333",
                                fontWeight: "bold"
                            }}
                        >
                            U
                        </span>
                    )}

                </Badge>

            </Tooltip>
        );
    }


    if (alert === 1) {

        return (
            <Tooltip
                placement="topLeft"
                title={i18next.t("events.ok")}
                overlayStyle={{
                    fontSize: "13px"
                }}
            >

                <Badge
                    color="light-warning"
                    className="badge-icon-lg"
                >

                    {text && (
                        <span className="d-flex align-items-center">

                            <span
                                style={{
                                    color: "red",
                                    fontWeight: "bold"
                                }}
                            >
                                S
                            </span>

                            <span
                                style={{
                                    marginLeft: "5px"
                                }}
                            >
                                {text}
                            </span>

                        </span>
                    )}

                    {!text && (
                        <span
                            style={{
                                color: "red",
                                fontWeight: "bold",
                                fontSize: "18px",
                                lineHeight: "18px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            S
                        </span>
                    )}

                </Badge>

            </Tooltip>
        );
    }


    if (alert === 0) {
        return "-";
    }


    if (!config.hideEventOk) {

        return (
            <Tooltip
                placement="topLeft"
                title={i18next.t("events.ok")}
                overlayStyle={{
                    fontSize: "13px"
                }}
            >

                <Badge
                    color="light-success"
                    className="badge-icon-lg"
                >

                    {text && (
                        <span className="d-flex align-items-center">

                            <ThumbsUp />

                            <span
                                style={{
                                    marginLeft: "5px"
                                }}
                            >
                                {text}
                            </span>

                        </span>
                    )}

                    {!text && (
                        <ThumbsUp />
                    )}

                </Badge>

            </Tooltip>
        );
    }


    return (
        <Tooltip
            placement="topLeft"
            title={i18next.t(
                "events.no_reading"
            )}
            overlayStyle={{
                fontSize: "13px"
            }}
        >

            <Badge
                color="light-danger"
                className="badge-icon-lg"
            >

                {text && (
                    <span className="d-flex align-items-center">

                        <TbDatabaseExclamation />

                        <span
                            style={{
                                marginLeft: "5px"
                            }}
                        >
                            {text}
                        </span>

                    </span>
                )}

                {!text && (
                    <TbDatabaseExclamation />
                )}

            </Badge>

        </Tooltip>
    );
}


/*
 * ============================================================
 * GLOBAL PLANT STATUS
 * ============================================================
 */

export function getIconByGlobalPlantStatus(
    config,
    row,
    text = undefined
) {

    let alert =
        parseInt(
            row.plant_status_id
        );


    if (isNaN(alert)) {
        alert = 0;
    }


    const wrapperIconStyle = {

        display:
            "flex",

        flexDirection:
            "row",

        alignContent:
            "center",

        justifyContent:
            "center",

        alignItems:
            "center",

        width:
            "30px",

        height:
            "30px",

        borderRadius:
            "0.358rem",

        paddingLeft:
            "1px"
    };


    /*
     * Estados:
     *
     * 0 = NO_SINCRONIZADO
     * 1 = OK
     * 2 = AVISO
     * 3 = ERROR
     * 4 = PENDIENTE_ACEPTACION_OFERTA
     * 6 = SINCRONIZADO
     */

    const isOfferPending =
        _.get(
            row,
            "plant_manual_status",
            -1
        ) === 1 ||
        alert === 4;


    if (isOfferPending) {

        const Dot = (

            <sup
                data-show="true"
                className="ant-scroll-number ant-badge-dot ant-badge-status-warning"
                style={{
                    top: "6px",
                    right: "6px"
                }}
            />
        );


        return (
            <Tooltip
                placement="topLeft"
                title={i18next.t(
                    "events.offer_pending_approval"
                )}
                overlayStyle={{
                    fontSize: "13px"
                }}
            >

                <AntdBadge
                    className="bg-approval-offer"
                    count={Dot}
                >

                    <div
                        className="bg-approval-offer"
                        style={
                            wrapperIconStyle
                        }
                    >

                        <AiOutlineSignature
                            color="rgb(129 129 129)"
                            size={19}
                        />

                    </div>

                </AntdBadge>

            </Tooltip>
        );
    }


    if (
        alert === 1 ||
        alert === 6
    ) {

        return (
            <Tooltip
                placement="topLeft"
                title={i18next.t(
                    "events.ok"
                )}
                overlayStyle={{
                    fontSize: "13px"
                }}
            >

                <AntdBadge
                    className="bg-light-success"
                >

                    <div
                        className="bg-light-success"
                        style={
                            wrapperIconStyle
                        }
                    >

                        <ThumbsUp
                            size={19}
                        />

                    </div>

                </AntdBadge>

            </Tooltip>
        );
    }


    if (
        alert === 2 ||
        alert === 0
    ) {

        return (
            <Tooltip
                placement="topLeft"
                title={i18next.t(
                    "events.warning"
                )}
                overlayStyle={{
                    fontSize: "13px"
                }}
            >

                <AntdBadge
                    className="bg-light-warning"
                >

                    <div
                        className="bg-light-warning"
                        style={
                            wrapperIconStyle
                        }
                    >

                        <AlertTriangle
                            size={19}
                        />

                    </div>

                </AntdBadge>

            </Tooltip>
        );
    }


    if (alert === 3) {

        return (
            <Tooltip
                placement="topLeft"
                title={i18next.t(
                    "events.fail"
                )}
                overlayStyle={{
                    fontSize: "13px"
                }}
            >

                <AntdBadge
                    className="bg-light-danger"
                >

                    <div
                        className="bg-light-danger"
                        style={
                            wrapperIconStyle
                        }
                    >

                        <AiOutlineAlert
                            size={19}
                        />

                    </div>

                </AntdBadge>

            </Tooltip>
        );
    }


    return (
        <Tooltip
            placement="topLeft"
            title={i18next.t(
                "events.fail"
            )}
            overlayStyle={{
                fontSize: "13px"
            }}
        >

            <AntdBadge
                className="bg-light-danger"
            >

                <div
                    className="bg-light-danger"
                    style={
                        wrapperIconStyle
                    }
                >

                    <AiOutlineAlert
                        size={19}
                    />

                </div>

            </AntdBadge>

        </Tooltip>
    );
}


/*
 * ============================================================
 * COLOR BY EVENT
 * ============================================================
 */

export function getColorByEvent(eventId) {

    eventId =
        parseInt(eventId);


    if (eventId === 1) {
        return "#28c76f";
    }

    if (eventId === 2) {
        return "#ea5455";
    }

    if (eventId === 5) {
        return "#00cfe8";
    }

    if (eventId === 6) {
        return "#7367f0";
    }

    if (eventId === 7) {
        return "#ff9f43";
    }

    if (eventId === 8) {
        return "#82868b";
    }
}


/*
 * ============================================================
 * COLOR BY STATUS
 * ============================================================
 */

export function getColorByStatus(eventId) {

    if (
        eventId === "1" ||
        eventId === "6"
    ) {
        return "#28c76f";
    }


    if (
        eventId === "2" ||
        eventId === "0" ||
        eventId === "4"
    ) {
        return "#ff9f43";
    }


    if (eventId === "3") {
        return "#ea5455";
    }


    return "#ea5455";
}


/*
 * ============================================================
 * CLEAN URL
 * ============================================================
 */

export function getTextUrlClean(text) {

    try {

        if (
            typeof text === "string" ||
            text instanceof String
        ) {

            text =
                cleanTextUtils
                    .replace
                    .diacritics(text);

            text =
                cleanTextUtils
                    .replace
                    .smartChars(text);

            text =
                text.toLowerCase();


            return textCleaner(text)
                .removeChars({
                    exclude:
                        "ñ1234567890"
                })
                .condense()
                .trim()
                .remove(" ")
                .valueOf()
                .replaceAll(" ", "")
                .replaceAll("ñ", "n");
        }


        return "plant";

    } catch (error) {

        return "plant";
    }
}


/*
 * ============================================================
 * AUX ENERGY
 * ============================================================
 */

export function getAuxEnergyTranslationKey(id) {

    if (id === 1) {
        return "aux_energy.electricity";
    }

    if (id === 2) {
        return "aux_energy.natural_gas";
    }

    if (id === 3) {
        return "aux_energy.diesel";
    }
}


export function getAuxEnergyTechTranslationKey(id) {

    if (id === 1) {
        return "aux_energy.resistance";
    }

    if (id === 2) {
        return "aux_energy.heat_pump";
    }

    if (id === 3) {
        return "aux_energy.conventional_boiler";
    }

    if (id === 4) {
        return "aux_energy.low_temperature_boiler";
    }

    if (id === 5) {
        return "aux_energy.condensing_boiler";
    }
}


/*
 * ============================================================
 * PRODUCTION VALUE
 * ============================================================
 */

export function getProductionColumnValueByCaller(
    config,
    value
) {

    if (
        isSolarThermalByCaller(config)
    ) {
        return value;
    }


    if (
        isSolarPhotovoltaicByCaller(config)
    ) {
        return value / 1000;
    }
}


/*
 * ============================================================
 * PRODUCTION TABLE COLUMNS
 * ============================================================
 */

export function getProductionColumnsTableByTime(
    config,
    rows
) {

    /*
     * HOURLY
     */
    if (
        config.time ===
        "time.hourly"
    ) {

        const columns = [

            getColumnTableProduction(
                config,
                "datefull"
            ),

            getColumnTableProduction(
                config,
                "production"
            ),

            getColumnTableProduction(
                config,
                "status"
            )
        ];


        if (
            isSolarThermalByCaller(
                config
            )
        ) {

            columns.push(
                getColumnTableProduction(
                    config,
                    "second_reading"
                )
            );
        }


        return columns;
    }


    /*
     * DAILY
     */
    if (
        config.time ===
        "time.daily"
    ) {

        const columns = [

            getColumnTableProduction(
                config,
                "datefull"
            ),

            getColumnTableProduction(
                config,
                "production"
            )
        ];


        if (
            isWaterByCaller(config)
        ) {

            columns.push(
                getColumnTableProduction(
                    config,
                    "eco_savings"
                )
            );

            columns.push(
                getColumnTableProduction(
                    config,
                    "status"
                )
            );

            return columns;
        }


        columns.push(
            getColumnTableProduction(
                config,
                "eco_savings"
            )
        );


        columns.push(
            getColumnTableProduction(
                config,
                "co2_savings"
            )
        );


        columns.push(
            getColumnTableProduction(
                config,
                "status"
            )
        );


        if (
            isSolarThermalByCaller(
                config
            )
        ) {

            columns.push(
                getColumnTableProduction(
                    config,
                    "second_reading"
                )
            );
        }


        return columns;
    }


    /*
     * MONTHLY
     */
    if (
        config.time ===
        "time.monthly"
    ) {

        if (
            isWaterByCaller(config)
        ) {

            return [

                getColumnTableProduction(
                    config,
                    "datefull"
                ),

                getColumnTableProduction(
                    config,
                    "production"
                ),

                getColumnTableProduction(
                    config,
                    "eco_savings"
                )
            ];
        }


        return [

            getColumnTableProduction(
                config,
                "datefull"
            ),

            getColumnTableProduction(
                config,
                "production"
            ),

            getColumnTableProduction(
                config,
                "eco_savings"
            ),

            getColumnTableProduction(
                config,
                "co2_savings"
            )
        ];
    }


    return [];
}


/*
 * ============================================================
 * INDEX TABLE
 * ============================================================
 *
 * CLAVE DEL NUEVO MODO ELIMINAR:
 *
 * No existe ya un listado especial.
 *
 * El listado normal mantiene exactamente las mismas
 * columnas y datos.
 *
 * Únicamente sustituimos:
 *
 *      getGlobalStatusColumn()
 *
 * por:
 *
 *      getDeleteColumn()
 *
 * cuando configuration.deleteMode === true.
 *
 * ============================================================
 */

export function getIndexTableByType(
    configuration,
    actions
) {

    /*
     * La columna de la derecha será:
     *
     * NORMAL:
     *      Estado
     *
     * DELETE MODE:
     *      Papelera
     */
    const statusOrDeleteColumn =
        configuration.deleteMode
            ? getDeleteColumn(
                configuration,
                actions
            )
            : getGlobalStatusColumn(
                configuration
            );


    /*
     * ========================================================
     * SOLAR TÉRMICA
     * ========================================================
     */

    if (
        isSolarThermalByCaller(
            configuration
        )
    ) {

        const columns = [

            getNameColumn(
                configuration,
                actions
            ),

            getLocationColumn(),

            getPerformanceColumn(
                configuration
            ),

            statusOrDeleteColumn
        ];


        /*
         * Las acciones normales se esconden
         * mientras estamos eliminando.
         */
        if (
            !configuration.deleteMode
        ) {

            columns.push(
                getActionsColumn(
                    configuration,
                    actions
                )
            );
        }


        return columns;
    }


    /*
     * ========================================================
     * SOLAR FOTOVOLTAICA
     * ========================================================
     */

    if (
        isSolarPhotovoltaicByCaller(
            configuration
        )
    ) {

        const columns = [

            getNameColumn(
                configuration,
                actions
            ),

            getLocationColumn(),

            getPerformanceColumn(
                configuration
            ),

            statusOrDeleteColumn
        ];


        if (
            !configuration.deleteMode
        ) {

            columns.push(
                getActionsColumn(
                    configuration,
                    actions
                )
            );
        }


        return columns;
    }


    /*
     * ========================================================
     * WATER
     * ========================================================
     */

    if (
        isWaterByCaller(
            configuration
        )
    ) {

        const columns = [

            getNameColumn(
                configuration,
                actions
            ),

            getLocationColumn(),

            getProductionColumn(
                configuration
            ),

            getMaxColumn(
                configuration
            ),

            statusOrDeleteColumn
        ];


        if (
            !configuration.deleteMode
        ) {

            columns.push(
                getActionsColumn(
                    configuration,
                    actions
                )
            );
        }


        return columns;
    }


    /*
     * ========================================================
     * THERMAL / TEMPERATURE
     * ========================================================
     */

    if (
        isThermalByCaller(
            configuration
        )
    ) {

        const columns = [

            getNameColumn(
                configuration,
                actions
            ),

            getLocationColumn(),

            getMinColumn(
                configuration
            ),

            getProductionColumn(
                configuration
            ),

            getMaxColumn(
                configuration
            ),

            statusOrDeleteColumn
        ];


        if (
            !configuration.deleteMode
        ) {

            columns.push(
                getActionsColumn(
                    configuration,
                    actions
                )
            );
        }


        return columns;
    }


    return [];
}